import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Api from "../../services/Api";
import {getLanguage} from "../../lang/helpers";
import {useNavigate, useParams} from "react-router";
import iconPlus from "url:shared-assets/icons/plus.svg";
import Loading from "../../components/Loading";
import {UploadFile} from "../AiThreads/interfaces";
import animationProcessing from "url:shared-assets/animations/processing.svg";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";

interface CustomerSurveyInterface {
  id: string;
  name: string;
}

const AssessmentUpload = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [customerSurvey, setCustomerSurvey] = useState<CustomerSurveyInterface|null>(null);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    getData();
  }, []);

  const processFile = (_file: File): Promise<UploadFile | null> => {
    return new Promise((resolve) => {
      if (!_file) return resolve(null);
      if (_file.size > 12000000) {
        // setChatError(ChatError.FILE_TOO_LARGE);
        alert("File too large");
        return resolve(null);
      }
      let filetype = "";
      if (_file.type === "application/pdf") filetype = "pdf";
      else if (_file.type.match("image.*")) filetype = "image";
      else if (
        _file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        _file.type === "application/vnd.ms-excel"
      )
        filetype = "excel";
      else if (
        _file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        _file.type === "application/msword"
      )
        filetype = "word";
      else {
        alert("Bad file format")
        // setChatError(ChatError.BAD_FILE_FORMAT);
        return resolve(null);
      }
      const filename = _file.name;
      if (filetype === "excel") {
        const reader = new FileReader();
        reader.onload = (e) => {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const uint8Array = new Uint8Array(arrayBuffer);
          const binaryString = Array.from(uint8Array)
            .map((byte) => String.fromCharCode(byte))
            .join("");
          const base64String = btoa(binaryString);
          resolve({
            filename,
            filetype,
            bytes: base64String,
            length: base64String.length,
          });
        };
        reader.readAsArrayBuffer(_file);
      } else if (filetype === "word") {
        const reader = new FileReader();
        reader.onload = (e) => {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const uint8Array = new Uint8Array(arrayBuffer);
          const binaryString = Array.from(uint8Array)
            .map((byte) => String.fromCharCode(byte))
            .join("");
          const base64String = btoa(binaryString);
          resolve({
            filename,
            filetype,
            bytes: base64String,
            length: base64String.length,
          });
        };
        reader.readAsArrayBuffer(_file);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            filename,
            filetype,
            bytes: e.target?.result as string,
            length: (e.target?.result as string).length,
          });
        };
        reader.readAsDataURL(_file);
      }
    });
  };

  useEffect(() => {
    if (uploadFile) {
      processFile(uploadFile).then(uploadedFile => {
        if (uploadedFile) {
          const data = {
            "file": uploadedFile
          }
          setUploading(true);
          api.post("client/assessments/customer_survey_upload/" + id, data).then(response => {
            console.log("uploaded");
            setUploading(false);
            navigate("/assessments/customer_survey_report/" + id);
          })
        }
      });
    }
  }, [uploadFile])


  const getData = () => {
    api.get("client/assessments/customer_survey/" + id).then(response => {
      setCustomerSurvey(response.json.customerSurvey);
      setLoaded(true);
    })
  }

  if (!loaded) {
    return <Loading />;
  }

  if (!customerSurvey) {
    return <div className="mt-4">Error loading survey</div>
  }

  const handleFilesSelected = (file: File) => {
    setUploadFile(file);
  };

  const dropFile = (evt: any) => {
    evt.stopPropagation();
    evt.preventDefault();
    if (evt.dataTransfer.files.length > 0) {
      handleFilesSelected(evt.dataTransfer.files[0]);
    } else {
      alert("Error: We could not find any dropped files.");
    }
  };

  const crumbs = [
    {name: t("assessments"), url: "/assessments"},
    {name: "Upload report for " + customerSurvey.name},
  ]

  return (
    <div className="main-one-col small">
      <LocalBreadcrumbs crumbs={crumbs}/>
      <div className="z-h2 mt-4">{customerSurvey.name}</div>
      <div className="-body1">Upload your {customerSurvey.name} report below.</div>

      {
        uploading ? (
          <div className="mt-12 mx-auto">
            <img
              src={animationProcessing}
              className="mx-auto"
              alt="loading"
              style={{height: 16, width: 114}}
            />
            <div className="mt-4 text-center z-body2 mx-auto">
              Digesting your report - this might take a minute...
            </div>
          </div>
        ) : (
          <div className="mt-8 border border-dashed border-indigo-400 p-4 flex justify-center items-center rounded-md h-40"
               onDrop={(e) => {
                 e.currentTarget.classList.remove("bg-indigo-800");
                 dropFile(e);
               }}
               onDragOver={(e) => {
                 e.stopPropagation();
                 e.preventDefault();
                 e.currentTarget.classList.add("bg-indigo-800");
               }}
               onDragLeave={(e) => {
                 e.stopPropagation();
                 e.preventDefault();
                 e.currentTarget.classList.remove("bg-indigo-800");
               }}
          >
            <input
              id="profileFile"
              type="file"
              className="hidden"
              onChange={(e: any) => handleFilesSelected(e.target.files[0])}
            />
            <label htmlFor="profileFile" className="flex items-center cursor-pointer" style={{margin: 0}}>
              <img src={iconPlus} alt="icon" className="h-4 w-4"/>
              <span className="hidden lg:block ml-2 text-sm text-gray-400">
                {lang === "sv" ? "Ladda upp" : "Upload"}
              </span>
            </label>
          </div>
        )
      }


    </div>
  );
}

export default AssessmentUpload;